import axios from 'axios';

import { API_CONFIG } from '../constants/config';

const CSRF_HEADER = 'x-csrf-token';
const CSRF_TOKEN_URL = '/api/csrf-token';
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const IS_DEV = process.env.NODE_ENV !== 'production';

export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let csrfToken: string | null = null;
let csrfTokenRequest: Promise<string> | null = null;
let csrfTokenGeneration = 0;

const isMutatingRequest = (method?: string) =>
  MUTATING_METHODS.has((method || 'get').toLowerCase());

const isCsrfTokenRequest = (url?: string) =>
  Boolean(url && (url === CSRF_TOKEN_URL || url.endsWith(CSRF_TOKEN_URL)));

const setHeader = (headers: any, key: string, value: string) => {
  if (headers && typeof headers.set === 'function') {
    headers.set(key, value);
    return headers;
  }
  return { ...(headers || {}), [key]: value };
};

type RequestDebugMeta = {
  startedAt?: number;
  requestKey?: string;
};

const requestDebugCounts = new Map<string, number>();
const requestDebugTimestamps = new Map<string, number[]>();
const REQUEST_BURST_WINDOW_MS = 10_000;
const REQUEST_BURST_THRESHOLD = 5;

const getRequestKey = (method?: string, url?: string) => {
  const normalizedMethod = (method || 'get').toUpperCase();
  const normalizedUrl = String(url || '').split('?')[0] || 'unknown-url';
  return `${normalizedMethod} ${normalizedUrl}`;
};

const logRequestCount = (key: string) => {
  if (!IS_DEV) return;
  const nextCount = (requestDebugCounts.get(key) || 0) + 1;
  requestDebugCounts.set(key, nextCount);
  const now = Date.now();
  const recent = (requestDebugTimestamps.get(key) || []).filter((timestamp) => now - timestamp <= REQUEST_BURST_WINDOW_MS);
  recent.push(now);
  requestDebugTimestamps.set(key, recent);
  // Helps spot duplicate endpoint calls while navigating admin pages.
  console.info(`[api-debug] ${key} (#${nextCount})`);
  if (recent.length > REQUEST_BURST_THRESHOLD) {
    console.warn(
      `[api-debug] High request rate for ${key}: ${recent.length} calls within ${REQUEST_BURST_WINDOW_MS / 1000}s`,
    );
  }
};

const logRequestResult = (key: string, startedAt?: number, status?: number, failed = false) => {
  if (!IS_DEV) return;
  const duration = typeof startedAt === 'number' ? `${Date.now() - startedAt}ms` : 'n/a';
  const state = failed ? 'failed' : 'done';
  const statusLabel = typeof status === 'number' ? status : 'n/a';
  console.info(`[api-debug] ${key} ${state} status=${statusLabel} duration=${duration}`);
};

export const applyCsrfToken = (token: string) => {
  csrfToken = token;
  apiClient.defaults.headers.common[CSRF_HEADER] = token;
};

export const ensureCsrfToken = async (forceRefresh = false): Promise<string> => {
  if (csrfToken && !forceRefresh) return csrfToken;

  if (!csrfTokenRequest || forceRefresh) {
    const requestGeneration = ++csrfTokenGeneration;
    csrfTokenRequest = apiClient
      .get<{ csrfToken: string }>(CSRF_TOKEN_URL)
      .then((res) => {
        if (requestGeneration === csrfTokenGeneration) {
          applyCsrfToken(res.data.csrfToken);
        }
        return res.data.csrfToken;
      })
      .finally(() => {
        if (requestGeneration === csrfTokenGeneration) {
          csrfTokenRequest = null;
        }
      });
  }

  return csrfTokenRequest!;
};

const SILENT_401_PATHS = new Set(['/api/auth/me', '/api/auth/login']);

let _onSessionExpired: (() => void) | null = null;
export const registerSessionExpiredHandler = (handler: () => void) => { _onSessionExpired = handler; };

// Automatically inject CSRF token before every mutating request.
apiClient.interceptors.request.use(async (config) => {
  if (IS_DEV) {
    const key = getRequestKey(config.method, config.url);
    (config as typeof config & RequestDebugMeta).requestKey = key;
    (config as typeof config & RequestDebugMeta).startedAt = Date.now();
    logRequestCount(key);
  }

  if (isMutatingRequest(config.method) && !isCsrfTokenRequest(config.url)) {
    const token = await ensureCsrfToken();
    config.headers = setHeader(config.headers, CSRF_HEADER, token);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    const debugConfig = response?.config as (typeof response.config & RequestDebugMeta) | undefined;
    if (debugConfig?.requestKey) {
      logRequestResult(debugConfig.requestKey, debugConfig.startedAt, response.status, false);
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error?.code;
    const message = error?.response?.data?.error?.message;
    const config = error?.config;
    const debugConfig = config as (typeof config & RequestDebugMeta) | undefined;
    if (debugConfig?.requestKey) {
      logRequestResult(debugConfig.requestKey, debugConfig.startedAt, status, true);
    }

    const url: string = error?.config?.url ?? '';
    const isSilent = SILENT_401_PATHS.has(url) || [...SILENT_401_PATHS].some((p) => url.endsWith(p));

    // On CSRF failure: fetch a fresh token and retry once.
    if (
      status === 403 &&
      (code === 'EBADCSRFTOKEN' || message === 'invalid csrf token') &&
      config &&
      !config._csrfRetry &&
      isMutatingRequest(config.method) &&
      !isCsrfTokenRequest(config.url)
    ) {
      config._csrfRetry = true;
      const token = await ensureCsrfToken(true);
      config.headers = setHeader(config.headers, CSRF_HEADER, token);
      return apiClient(config);
    }

    if (status === 401 && !isSilent && _onSessionExpired) _onSessionExpired();

    return Promise.reject(error);
  },
);

export default apiClient;
